import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

// Placeholder testimonials — easy to replace later
const QUOTES = [
  {
    quote:
      "The difference was immediate. My son moved better, felt more confident, and dropped his 40 time in a single season. This is a real system, not a gimmick.",
    name: "Kristen P.",
    role: "Parent · West Virginia",
  },
  {
    quote:
      "They tested me, built a plan around my exact weaknesses, and held me accountable every week. I train with a purpose now.",
    name: "Marcus D.",
    role: "Wide Receiver · College Commit",
  },
  {
    quote:
      "As a coach, I send our athletes here for speed work. The results show up on Friday nights. Coach JJ develops the whole athlete.",
    name: "Coach Ryan T.",
    role: "High School Program · Ohio Valley",
  },
];

export default function Testimonials() {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);

  const go = useCallback(
    (d) => {
      setDir(d);
      setI((prev) => (prev + d + QUOTES.length) % QUOTES.length);
    },
    []
  );

  useEffect(() => {
    const t = setInterval(() => go(1), 7000);
    return () => clearInterval(t);
  }, [go, i]);

  const t = QUOTES[i];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-24 md:py-36 bg-black border-t border-white/10"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-10">
          What They're Saying
        </p>

        <div className="relative min-h-[280px] md:min-h-[240px] flex items-center justify-center">
          <span className="absolute -top-6 left-1/2 -translate-x-1/2 font-display text-[9rem] leading-none text-white/10 select-none">
            “
          </span>
          <AnimatePresence mode="wait" custom={dir}>
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 max-w-3xl"
              data-testid={`testimonial-${i}`}
            >
              <blockquote className="font-display uppercase tracking-tight text-3xl md:text-4xl leading-tight text-white">
                {t.quote}
              </blockquote>
              <figcaption className="mt-8">
                <p className="font-bold tracking-wide">{t.name}</p>
                <p className="font-mono-accent text-xs tracking-[0.2em] uppercase text-white/40 mt-1">
                  {t.role}
                </p>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-6 mt-12">
          <button
            data-testid="testimonial-prev"
            onClick={() => go(-1)}
            className="w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors duration-300"
            aria-label="Previous testimonial"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex gap-2">
            {QUOTES.map((_, idx) => (
              <button
                key={idx}
                data-testid={`testimonial-dot-${idx}`}
                onClick={() => { setDir(idx > i ? 1 : -1); setI(idx); }}
                className={`h-1.5 transition-all duration-300 ${
                  idx === i ? "w-8 bg-white" : "w-1.5 bg-white/30"
                }`}
                aria-label={`Go to testimonial ${idx + 1}`}
              />
            ))}
          </div>
          <button
            data-testid="testimonial-next"
            onClick={() => go(1)}
            className="w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors duration-300"
            aria-label="Next testimonial"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
